import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import {
  EmployeeStandardCellView,
  EmployeeStandardMatrixRequest,
  EmployeeStandardMatrixResponse,
  EmployeeStandardOverridesState,
  EmployeeStandardOverridesStatePatch,
  EmployeeStandardSelectableUser,
  EmployeeStandardUserResponse
} from '../../../../../../models/employee-standard-overrides.model';
import { EmployeeStandardOverridesService } from '../../../../../../services/employee-standard-overrides.service';
import { EmployeeStandardOverridesBulkRequestBuilder } from './employee-standard-overrides-bulk-request.builder';
import { EmployeeStandardOverridesMapper } from './employee-standard-overrides.mapper';
import { EmployeeStandardOverridesNotificationService } from './employee-standard-overrides-notification.service';

const EMPLOYEE_STANDARD_STEP_SELECT = 'SELECT';
const EMPLOYEE_STANDARD_STEP_MATRIX = 'MATRIX';

@Injectable()
export class EmployeeStandardOverridesFacade {
  public readonly state$: Observable<EmployeeStandardOverridesState>;
  private readonly stateSubject: BehaviorSubject<EmployeeStandardOverridesState>;
  private readonly destroy$ = new Subject<void>();
  private groupId = '';
  private matrixUserIds: string[] = [];

  constructor(
    private readonly apiService: EmployeeStandardOverridesService,
    private readonly mapper: EmployeeStandardOverridesMapper,
    private readonly bulkRequestBuilder: EmployeeStandardOverridesBulkRequestBuilder,
    private readonly notificationService: EmployeeStandardOverridesNotificationService
  ) {
    this.stateSubject = new BehaviorSubject<EmployeeStandardOverridesState>(this.createInitialState());
    this.state$ = this.stateSubject.asObservable();
  }

  public initialize(groupId: string): void {
    this.groupId = groupId;
    this.loadUsers();
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleUser(user: EmployeeStandardSelectableUser): void {
    var users = this.mapper.toggleSelectableUsers(this.getState().users, user);
    this.patchState({
      users: users,
      selectedCount: this.mapper.countSelectedUsers(users)
    });
  }

  public openMatrix(): void {
    this.matrixUserIds = this.createSelectedUserIds();
    if (!this.matrixUserIds.length) {
      this.notificationService.showNoSelection();
      return;
    }
    this.loadMatrix();
  }

  public openSelection(): void {
    this.patchState({ step: EMPLOYEE_STANDARD_STEP_SELECT });
  }

  public toggleCell(cell: EmployeeStandardCellView): void {
    var rows = this.mapper.toggleRows(this.getState().rows, cell);
    this.patchState({
      rows: rows,
      changedCount: this.mapper.countChangedCells(rows)
    });
  }

  public saveChanges(): void {
    var changes = this.mapper.collectChangedCells(this.getState().rows);
    if (!changes.length) {
      return;
    }
    this.sendChanges(changes);
  }

  private loadUsers(): void {
    this.patchState({ isUsersLoading: true });
    this.apiService.fetchGroupUsers(this.groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        (users) => this.handleUsersLoaded(users),
        (error) => this.handleLoadFailed(error)
      );
  }

  private handleUsersLoaded(users: EmployeeStandardUserResponse[]): void {
    var selectableUsers = this.mapper.mapSelectableUsers(users);
    this.patchState({
      users: selectableUsers,
      selectedCount: this.mapper.countSelectedUsers(selectableUsers),
      isUsersLoading: false
    });
  }

  private loadMatrix(): void {
    this.patchState(this.createMatrixLoadingPatch());
    this.apiService.fetchMatrix(this.groupId, this.createMatrixRequest())
      .takeUntil(this.destroy$)
      .subscribe(
        (matrix) => this.handleMatrixLoaded(matrix),
        (error) => this.handleLoadFailed(error)
      );
  }

  private handleMatrixLoaded(matrix: EmployeeStandardMatrixResponse): void {
    var users = this.resolveMatrixUsers(matrix);
    this.patchState({
      matrixUsers: this.mapper.mapMatrixUsers(users),
      rows: this.mapper.createRows(matrix, users),
      isMatrixLoading: false,
      isSaving: false
    });
  }

  private sendChanges(changes: EmployeeStandardCellView[]): void {
    this.patchState({ isSaving: true });
    Observable.forkJoin(this.createUpdateRequests(changes))
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleSaveSucceeded(),
        (error) => this.handleSaveFailed(error)
      );
  }

  private handleSaveSucceeded(): void {
    this.notificationService.showSaveSuccess();
    this.loadMatrix();
  }

  private handleSaveFailed(error: HttpErrorResponse): void {
    this.patchState({ isSaving: false });
    this.notificationService.showSaveError(error);
  }

  private handleLoadFailed(error: HttpErrorResponse): void {
    this.patchState({
      isUsersLoading: false,
      isMatrixLoading: false,
      isSaving: false
    });
    this.notificationService.showLoadError(error);
  }

  private createUpdateRequests(changes: EmployeeStandardCellView[]): Observable<string>[] {
    return this.bulkRequestBuilder.create(changes).map((request) => {
      return this.apiService.updateAssignments(this.groupId, request);
    });
  }

  private createSelectedUserIds(): string[] {
    return this.getState().users
      .filter((user) => user.isSelected)
      .map((user) => user.id);
  }

  private createMatrixRequest(): EmployeeStandardMatrixRequest {
    return {
      userIds: this.matrixUserIds.slice()
    };
  }

  private resolveMatrixUsers(matrix: EmployeeStandardMatrixResponse): EmployeeStandardUserResponse[] {
    if (!matrix || !matrix.users) {
      return [];
    }
    return matrix.users;
  }

  private createMatrixLoadingPatch(): EmployeeStandardOverridesStatePatch {
    return {
      step: EMPLOYEE_STANDARD_STEP_MATRIX,
      isMatrixLoading: true,
      changedCount: 0
    };
  }

  private patchState(patch: EmployeeStandardOverridesStatePatch): void {
    this.stateSubject.next(Object.assign({}, this.getState(), patch));
  }

  private getState(): EmployeeStandardOverridesState {
    return this.stateSubject.getValue();
  }

  private createInitialState(): EmployeeStandardOverridesState {
    return {
      users: [],
      matrixUsers: [],
      rows: [],
      selectedCount: 0,
      changedCount: 0,
      isUsersLoading: false,
      isMatrixLoading: false,
      isSaving: false,
      step: EMPLOYEE_STANDARD_STEP_SELECT
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/employee-standard-overrides/services/employee-standard-overrides.facade.ts